/** Cliente HTTP del servidor de la pizarra. */
import type { BoardSummary } from '../shared/types.ts'

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    headers: init?.body instanceof FormData ? {} : { 'content-type': 'application/json' },
    ...init,
  })
  if (!res.ok) {
    const detail = (await res.json().catch(() => null)) as LoginError | null
    const message = detail?.error ?? `Error ${res.status}`
    // 401 conserva su tipo propio: la app lo usa para volver a la pantalla de acceso.
    throw res.status === 401
      ? new UnauthorizedError(message, detail)
      : new ApiError(message, res.status, detail)
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

export class ApiError extends Error {
  readonly status: number
  readonly detail: LoginError | null

  constructor(message: string, status: number, detail: LoginError | null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
  }
}

export class UnauthorizedError extends ApiError {
  constructor(message = 'No autorizado', detail: LoginError | null = null) {
    super(message, 401, detail)
    this.name = 'UnauthorizedError'
  }
}

export interface LoginState {
  codeLength: number
  numeric: boolean
  emailEnabled: boolean
  emailDomain: string
  captchaRequired: boolean
  locked: boolean
  retryAfterMs: number
  attemptsLeft: number
}

export interface LoginError {
  error: string
  captchaRequired?: boolean
  attemptsLeft?: number
  locked?: boolean
  retryAfterMs?: number
}

export interface SessionInfo {
  authenticated: boolean
  name: string
  userId: string
  readOnly?: boolean
}

export const api = {
  session: (): Promise<SessionInfo> => request('/api/session'),

  loginState: (): Promise<LoginState> => request('/api/login/state'),

  captcha: (): Promise<{ id: string; svg: string }> => request('/api/captcha'),

  requestEmailCode: (email: string, captchaId?: string, captchaAnswer?: string): Promise<{ sent: boolean }> =>
    request('/api/login/email', { method: 'POST', body: JSON.stringify({ email, captchaId, captchaAnswer }) }),

  login: (params: {
    pin?: string
    name?: string
    email?: string
    code?: string
    captchaId?: string
    captchaAnswer?: string
  }): Promise<SessionInfo> => request('/api/login', { method: 'POST', body: JSON.stringify(params) }),

  setName: (name: string): Promise<SessionInfo> =>
    request('/api/session/name', { method: 'POST', body: JSON.stringify({ name }) }),

  listBoards: (): Promise<BoardSummary[]> => request('/api/boards'),

  createBoard: (name: string, kind: 'board' | 'mindmap' = 'board'): Promise<BoardSummary> =>
    request('/api/boards', { method: 'POST', body: JSON.stringify({ name, kind }) }),

  getBoard: (id: string): Promise<BoardSummary & { role: 'edit' | 'view' }> => request(`/api/boards/${id}`),

  renameBoard: (id: string, name: string): Promise<void> =>
    request(`/api/boards/${id}`, { method: 'PATCH', body: JSON.stringify({ name }) }),

  deleteBoard: (id: string): Promise<void> => request(`/api/boards/${id}`, { method: 'DELETE' }),

  duplicateBoard: (id: string): Promise<BoardSummary> =>
    request(`/api/boards/${id}/duplicate`, { method: 'POST' }),

  saveThumb: (id: string, dataUrl: string): Promise<void> =>
    request(`/api/boards/${id}/thumb`, { method: 'POST', body: JSON.stringify({ thumb: dataUrl }) }),

  createShare: (id: string, role: 'edit' | 'view'): Promise<{ url: string }> =>
    request(`/api/boards/${id}/share`, { method: 'POST', body: JSON.stringify({ role }) }),

  upload: async (file: Blob, filename: string, boardId?: string): Promise<{ url: string; width: number; height: number }> => {
    const form = new FormData()
    form.append('file', file, filename)
    // Con ?board= el servidor autoriza por el permiso del tablero: un invitado
    // con enlace de edición no es "miembro" y sin esto recibía 403.
    const url = boardId ? `/api/upload?board=${encodeURIComponent(boardId)}` : '/api/upload'
    return request(url, { method: 'POST', body: form })
  },
}
